import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import AnlassLayout from "@/components/anlass/AnlassLayout";
import GeburtstagInhalt from "@/components/anlass/GeburtstagInhalt";
import { loadCatalog } from "@/lib/catalog";
import { buildAnlassBoxen } from "@/utils/buildPopularBoxes";
import { anlassAnalyse, ohneBoxen } from "@/utils/anlassAnalyse";
import { geburtstagFaq } from "@/utils/anlassFaq";
import { euro } from "@/utils/preisAnalyse";

/**
 * Geburtstag: 6.162 Impressionen in 90 Tagen auf Position 21, vier Klicks.
 *
 * Anders als bei der Hochzeit ist hier ein Paket ohne Drucker eine ernsthafte
 * Option — die Feiern sind kleiner, und "günstige fotobox mieten für kleine
 * geburtstagsfeiern" ist eine der Anfragen, die tatsächlich gestellt werden.
 * Deshalb rechnet die Seite mit 40 Gästen und weist beide Wege aus.
 */

const GAESTE = 40;

export async function getStaticProps({ locale }) {
  const json = await loadCatalog();
  const boxen = json.Photobooths || [];

  const analyse = anlassAnalyse(boxen, { gaeste: GAESTE, druckNoetig: true });
  const digital = anlassAnalyse(boxen, { gaeste: GAESTE, druckNoetig: false });

  const passendeBoxen = analyse?.boxen || [];
  const popularBoxes = buildAnlassBoxen(passendeBoxen, 6);

  const stand = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return {
    props: {
      analyse: ohneBoxen(analyse),
      digital: digital ? { min: digital.min, median: digital.median } : null,
      popularBoxes,
      stand,
      ...(await serverSideTranslations(locale, ["common"])),
    },
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}

export default function GeburtstagSeite({
  analyse,
  digital,
  popularBoxes,
  stand,
}) {
  const abPreis = digital?.min || analyse?.min || 0;

  return (
    <AnlassLayout
      seo={{
        titel: abPreis
          ? `Fotobox für den Geburtstag mieten ab ${euro(abPreis)} € – im Vergleich`
          : "Fotobox für den Geburtstag mieten – Preise & Anbieter",
        beschreibung: analyse
          ? `Fotobox für den Geburtstag mieten: Pakete für ${analyse.gaeste} Gäste ab ${euro(analyse.min)} €, ohne Drucker ab ${euro(abPreis)} €. Passt auch in die Wohnung — jetzt vergleichen.`
          : "Fotobox für den Geburtstag mieten: Preise, Pakete und Anbieter im Vergleich.",
        pfad: "/fotobox-mieten-geburtstag",
      }}
      breadcrumb="Fotobox für den Geburtstag"
      hero={{
        titel: "Fotobox für den Geburtstag mieten",
        lead: "Von der Feier zu Hause bis zum runden Geburtstag — vergleichen und direkt buchen.",
        anzahlBoxen: analyse?.anzahlBoxen || 0,
        anzahlVersand: analyse?.perVersand || 0,
        anzahlRegional: analyse?.bauenAuf || 0,
        abPreis,
        hinweis: (
          <>
            {abPreis > 0 && (
              <>
                Ab{" "}
                <strong className="font-semibold text-white">
                  {euro(abPreis)} €
                </strong>
                .{" "}
              </>
            )}
            Aufbau in einer Viertelstunde, eine normale Steckdose genügt.
          </>
        ),
      }}
      angebote={{
        titel: "Fotoboxen für eine Geburtstagsfeier",
        untertitel: analyse
          ? `Ausgewählt aus den Angeboten, die für ${analyse.gaeste} Gäste genug Ausdrucke liefern.`
          : "Ausgewählt nach Druckleistung und Ausstattung.",
        items: popularBoxes,
      }}
      faq={geburtstagFaq(analyse, digital)}
      faqTitel="Häufige Fragen zur Fotobox auf dem Geburtstag"
      faqUntertitel="Die Fragen, die uns über die Suche am häufigsten erreichen."
      staedteTitel="Fotobox für den Geburtstag mieten in deiner Stadt"
      abschlussTitel="Jetzt die passende Fotobox für den Geburtstag finden"
    >
      <GeburtstagInhalt analyse={analyse} digital={digital} stand={stand} />
    </AnlassLayout>
  );
}
